import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface Cleaner {
  id: number;
  name: string;
  scheduleDays: number;
  apartmentIds: number[];
  privateKey: string;
}

export interface CleanerRequest {
  name: string;
  scheduleDays: number;
  apartmentIds: number[];
}

export interface CleaningScheduleEntry {
  date: string;               // ISO date "YYYY-MM-DD"
  reservationId: number;
  apartmentName: string;
  leavingGuests: number | null;  // null = not known yet
  arrivingGuests: number | null; // null = unknown
  estimatedArrivalTime: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'DONE';
  notes: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /**
   * The window this turnover actually runs between, including any extra hours either guest has
   * bought. A late check-out nobody told the cleaner about is the surprise the whole feature is
   * designed to avoid, so it arrives on the same row as everything else rather than as an alert.
   */
  departureTime: string | null;
  arrivalTime: string | null;
  departureExtended: boolean;
  arrivalBroughtForward: boolean;
}

export interface CleaningSchedule {
  cleanerName: string;
  entries: CleaningScheduleEntry[];
}

// ── Manager endpoints (auth required) ────────────────────────────────────────

export const getCleaners = async (token: string): Promise<Cleaner[]> => {
  const res = await axios.get(`${API_BASE}/cleaners`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const createCleaner = async (payload: CleanerRequest, token: string): Promise<Cleaner> => {
  const res = await axios.post(`${API_BASE}/cleaners`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const updateCleaner = async (id: number, payload: CleanerRequest, token: string): Promise<Cleaner> => {
  const res = await axios.put(`${API_BASE}/cleaners/${id}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deleteCleaner = async (id: number, token: string): Promise<void> => {
  await axios.delete(`${API_BASE}/cleaners/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

// ── Public endpoints (no auth, keyed by privateKey) ───────────────────────────

export const getCleaningSchedule = async (privateKey: string): Promise<CleaningSchedule> => {
  const res = await axios.get(`${API_BASE}/public/cleaning/${privateKey}`);
  return res.data;
};

export const startCleaningTask = async (
  privateKey: string, reservationId: number
): Promise<CleaningScheduleEntry> => {
  const res = await axios.post(`${API_BASE}/public/cleaning/${privateKey}/tasks/${reservationId}/start`);
  return res.data;
};

export const finishCleaningTask = async (
  privateKey: string, reservationId: number
): Promise<CleaningScheduleEntry> => {
  const res = await axios.post(`${API_BASE}/public/cleaning/${privateKey}/tasks/${reservationId}/finish`);
  return res.data;
};

export const updateCleaningTaskNotes = async (
  privateKey: string, reservationId: number, notes: string
): Promise<CleaningScheduleEntry> => {
  const res = await axios.put(
    `${API_BASE}/public/cleaning/${privateKey}/tasks/${reservationId}/notes`,
    { notes }
  );
  return res.data;
};
